import React from 'react';
import { btn, card, inp } from './ui';

// ── Dialog chrome shared by the writing panes (owner 2026-10-13) ────────────────────────────────
// Batch 2 (Banking) wrote the first dialogs of the workspace as fixed overlays with inline styles —
// there is no shared Modal in the repo. Batch 3 (Purchases) needs the same shapes, so they live here
// once instead of being copied pane by pane: the overlay dialog, a right-hand drawer for a record's
// detail, the field/label/button styles the forms use, the filter chip the shell's submenu row wears
// (index.tsx keeps its own private), a loader and the stat card. Nothing here fetches or computes.

export const r2 = (n: number) => Math.round(n * 100) / 100;
export const todayIso = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; };
export const isoDate = (s: string) => /^\d{4}-\d{2}-\d{2}$/.test(String(s || ''));

export const chip: React.CSSProperties = {
  padding: '5px 11px', fontSize: 12, fontWeight: 600, borderRadius: 999, border: '1px solid var(--border-strong)',
  background: 'var(--bg-surface)', color: 'var(--text-secondary)', cursor: 'pointer',
};
export const chipOn: React.CSSProperties = { background: 'var(--accent-bg)', color: 'var(--accent-text)', border: '1px solid var(--accent)' };
export const actBtn: React.CSSProperties = { ...btn, padding: '4px 9px', fontSize: 12 };
export const lbl: React.CSSProperties = { fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.03em', color: 'var(--text-secondary)', display: 'block', marginBottom: 5 };
export const field: React.CSSProperties = { ...inp, width: '100%' };
export const cancelBtn: React.CSSProperties = { ...btn, background: 'var(--c-gray-100)', color: 'var(--text-secondary)', border: 'none' };
export const emptyLine: React.CSSProperties = { padding: 26, textAlign: 'center', color: 'var(--text-tertiary)', fontSize: 13 };
export const Loader = () => <div style={{ padding: 26, textAlign: 'center', color: 'var(--text-tertiary)' }}><i className="ti ti-loader" /></div>;
export const Stat = ({ label, value, tone, sub }: { label: string; value: React.ReactNode; tone?: string; sub?: string }) => (
  <div style={{ ...card, minWidth: 150, flex: '1 1 150px' }}>
    <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: '.04em', textTransform: 'uppercase', color: 'var(--text-tertiary)' }}>{label}</div>
    <div style={{ fontSize: 18, fontWeight: 800, color: tone || 'var(--text-primary)', marginTop: 4, fontVariantNumeric: 'tabular-nums' }}>{value}</div>
    {sub && <div style={{ fontSize: 11.5, color: 'var(--text-tertiary)', marginTop: 2 }}>{sub}</div>}
  </div>
);

/** Fixed overlay + card, the repo's dialog shape (no shared Modal exists). Clicking the backdrop
 *  closes unless a request is in flight. zIndex above the Fab (100) so the dialog covers it. */
export function Dialog({ title, onClose, busy, width = 520, children }: { title: string; onClose: () => void; busy?: boolean; width?: number; children: React.ReactNode }) {
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 10002, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      onMouseDown={e => { if (e.target === e.currentTarget && !busy) onClose(); }}>
      <div style={{ width, maxWidth: '92vw', maxHeight: '90vh', overflowY: 'auto', background: 'var(--bg-surface)', borderRadius: 14, padding: 22, boxShadow: 'var(--shadow-md)', boxSizing: 'border-box' }}>
        <div style={{ fontSize: 16, fontWeight: 800, color: 'var(--text-primary)', marginBottom: 12 }}>{title}</div>
        {children}
      </div>
    </div>
  );
}

/** A record's detail beside the list: a panel pinned to the right edge over a lighter backdrop, so
 *  the list stays in view and a dialog (zIndex 10002) opened from the drawer still covers it. */
export function Drawer({ title, onClose, width = 560, children }: { title: React.ReactNode; onClose: () => void; width?: number; children: React.ReactNode }) {
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.25)', zIndex: 10001 }} onMouseDown={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{ position: 'absolute', top: 0, right: 0, bottom: 0, width, maxWidth: '96vw', background: 'var(--bg-surface)', boxShadow: 'var(--shadow-md)', overflowY: 'auto', padding: 20, boxSizing: 'border-box', display: 'flex', flexDirection: 'column', gap: 14 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ fontSize: 15, fontWeight: 800, color: 'var(--text-primary)', flex: 1, minWidth: 0 }}>{title}</div>
          <button onClick={onClose} style={{ ...actBtn, padding: '4px 7px' }} aria-label="close"><i className="ti ti-x" aria-hidden /></button>
        </div>
        {children}
      </div>
    </div>
  );
}
